import { useState, useCallback, useMemo } from 'react';
import {
  LocationConfig,
  IntegrationConfig,
  SubmitStatus,
} from '../types/AccuLynxTypes';
import { ACCULYNX_STAGES } from '../constants/AccuLynxConstants';
import { ACCULYNX_CONFIG_API_BASE } from 'application/config/apiConfig';
import { Auth } from 'aws-amplify';

/** Get ID token for API Gateway Cognito authorizer */
async function getAuthHeaders(): Promise<Record<string, string>> {
  const session = await Auth.currentSession();
  const token = session.getIdToken().getJwtToken();
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

export const useAccuLynxActions = (
  config: IntegrationConfig,
  setConfig: (
    config: IntegrationConfig | ((prev: IntegrationConfig) => IntegrationConfig)
  ) => void,
  paivStages: any[],
  circles: any[],
  isEditing: boolean,
  savedConfig: IntegrationConfig,
  setIsEditing: (editing: boolean) => void,
  setSavedConfig: (config: IntegrationConfig) => void,
  stageCategories: any[] = []
) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<SubmitStatus>({
    type: undefined,
    message: '',
  });

  // derived flags can be computed where needed

  // Add a new location
  const addLocation = useCallback(() => {
    console.log('Adding new location');
    console.log('Available stages:', paivStages);

    setConfig((prev) => {
      // Determine default stageCategoryID:
      // 1. Use the first existing location's stageCategoryID if available
      // 2. Otherwise, use the first category from stageCategories (usually "Contacts")
      const defaultStageCategoryID =
        prev.locations.find((loc) => loc?.stageCategoryID)?.stageCategoryID ||
        (stageCategories.length > 0 ? stageCategories[0]?.id : '');

      return {
        ...prev,
        locations: [
          ...prev.locations,
          {
            apiKey: '',
            paivStage: paivStages[0]?.title || '',
            paivStageId: paivStages[0]?.id || '',
            accessToken: '',
            refreshToken: '',
            tokenExpiration: '',
            connectionStatus: 'untested',
            stageCategoryID: defaultStageCategoryID,
            stageMappings: ACCULYNX_STAGES.map((stage) => ({
              accuLynxStage: stage,
              paivStage: '',
              paivStageId: '',
            })),
          },
        ],
      };
    });
  }, [paivStages, setConfig, stageCategories]);

  // Remove a location from local config
  const removeLocation = useCallback(
    (index: number) => {
      setConfig((prev) => ({
        ...prev,
        locations: prev.locations.filter((_, i) => i !== index),
      }));
    },
    [setConfig]
  );

  // Remove an individual integration from database
  const removeIntegration = useCallback(
    async (locationIndex: number, onConfirm: () => void): Promise<void> => {
      const location = config.locations[locationIndex];
      if (!location?.circleId) return;

      // Call the confirmation callback - the parent component will show the modal
      onConfirm();
    },
    [config.locations]
  );

  // Actually perform the integration removal after confirmation
  const performIntegrationRemoval = useCallback(
    async (locationIndex: number): Promise<void> => {
      try {
        const location = config.locations[locationIndex];
        const configId = (location as any)?.id;

        if (!configId) {
          // No saved record — just remove from local state
          const updatedLocations = config.locations.filter(
            (_, i) => i !== locationIndex
          );
          setConfig((prev) => ({ ...prev, locations: updatedLocations }));
          if (updatedLocations.length === 0) {
            setConfig({ locations: [], stageMappings: [] });
            setSavedConfig({ locations: [], stageMappings: [] });
            setIsEditing(false);
          }
          setSubmitStatus({
            type: 'success',
            message: 'Location removed',
          });
          return;
        }

        // Call DELETE endpoint for this specific config
        const res = await fetch(`${ACCULYNX_CONFIG_API_BASE}/${configId}`, {
          method: 'DELETE',
          headers: await getAuthHeaders(),
        });

        const result = await res.json();
        if (!res.ok || !result?.success) {
          throw new Error(result?.error || 'Failed to remove integration');
        }

        // Update local state
        const updatedLocations = config.locations.filter(
          (_, i) => i !== locationIndex
        );
        setConfig((prev) => ({ ...prev, locations: updatedLocations }));

        if (updatedLocations.length === 0) {
          setConfig({ locations: [], stageMappings: [] });
          setSavedConfig({ locations: [], stageMappings: [] });
          setIsEditing(false);
        } else {
          setSavedConfig({ ...savedConfig, locations: updatedLocations });
        }

        setSubmitStatus({
          type: 'success',
          message: 'Integration removed successfully',
        });
      } catch (error: any) {
        console.error('Error removing integration:', error);
        setSubmitStatus({
          type: 'error',
          message: error?.message || 'Failed to remove integration',
        });
      }
    },
    [
      config.locations,
      setConfig,
      setIsEditing,
      setSubmitStatus,
      setSavedConfig,
      savedConfig,
    ]
  );

  // Update location fields
  const updateLocation = useCallback(
    (index: number, field: keyof LocationConfig | PropertyKey, value: any) => {
      setConfig((prev) => {
        if (field === 'paivStage' && index === 0) {
          const selectedStage = paivStages.find(
            (stage) => stage.title === value
          );
          return {
            ...prev,
            locations: prev.locations.map((loc) => ({
              ...loc,
              paivStage: value,
              paivStageId: selectedStage?.id || '',
            })),
          };
        }

        // When selecting a team, update circleId only (preserve existing id)
        if (field === 'circleId') {
          return {
            ...prev,
            locations: prev.locations.map((loc, i) =>
              i === index
                ? {
                    ...loc,
                    circleId: value as string,
                  }
                : loc
            ),
          };
        }

        return {
          ...prev,
          locations: prev.locations.map((loc, i) =>
            i === index
              ? { ...loc, [field as keyof LocationConfig]: value }
              : loc
          ),
        };
      });
    },
    [paivStages, setConfig, circles]
  );

  // Handle API key changes
  const handleApiKeyChange = useCallback(
    (index: number, value: string) => {
      updateLocation(index, 'apiKey', value);
      // When user edits the key, mark as untested until save validates
      updateLocation(
        index,
        'connectionStatus',
        value.trim() ? 'untested' : 'untested'
      );
    },
    [updateLocation]
  );

  // Update stage mappings
  const updateStageMapping = useCallback(
    (
      locationIndex: number,
      accuLynxStage: string,
      paivStageTitle: string
    ): void => {
      console.log('Updating stage mapping:', {
        locationIndex,
        accuLynxStage,
        paivStageTitle,
        paivStagesCount: paivStages.length,
        paivStagesTitles: paivStages.map((s) => s.title),
      });

      const selectedStage = paivStages.find(
        (stage) => stage.title === paivStageTitle
      );
      console.log('Selected stage:', {
        selectedStage,
        foundById: selectedStage?.id,
        foundByTitle: selectedStage?.title,
        exactMatch: paivStages.find((s) => s.title === paivStageTitle),
        trimmedMatch: paivStages.find(
          (s) => s.title.trim() === paivStageTitle.trim()
        ),
      });

      setConfig((prev) => ({
        ...prev,
        locations: prev.locations.map((loc, idx) => {
          if (idx !== locationIndex) return loc;

          const existing = loc.stageMappings || [];
          const hasEntry = existing.some(
            (m) => m?.accuLynxStage === accuLynxStage
          );

          const nextStageMappings = hasEntry
            ? existing.map((m) =>
                m.accuLynxStage === accuLynxStage
                  ? {
                      ...m,
                      paivStage: paivStageTitle,
                      paivStageId: String(selectedStage?.id ?? ''),
                    }
                  : m
              )
            : [
                ...existing,
                {
                  accuLynxStage,
                  paivStage: paivStageTitle,
                  paivStageId: String(selectedStage?.id ?? ''),
                },
              ];

          console.log('Updated stage mappings:', {
            locationIndex: idx,
            accuLynxStage,
            newMapping: nextStageMappings.find(
              (m) => m.accuLynxStage === accuLynxStage
            ),
          });

          return {
            ...loc,
            stageMappings: nextStageMappings,
          };
        }),
      }));
    },
    [paivStages, setConfig]
  );

  // Validate configuration
  const validateConfig = useCallback((): boolean => {
    if (!config.locations || config.locations.length === 0) {
      return false;
    }

    // All required fields — just need an API key
    const fieldsOk = config.locations.every((loc) => {
      if (!loc) return false;
      if (!loc.apiKey?.trim()) return false;
      return true;
    });
    if (!fieldsOk) return false;

    return true;
  }, [config]);

  // Handle form submission
  const handleSubmit = useCallback(
    async (e?: React.FormEvent) => {
      if (e) e.preventDefault();

      setIsSubmitting(true);
      setSubmitStatus({ type: undefined, message: '' });

      try {
        const orgID = (window as any).me.orgID;
        const authHeaders = await getAuthHeaders();

        const results: any[] = [];
        const errors: any[] = [];
        const warnings: any[] = [];

        // Process each location individually via REST CRUD
        for (const location of config.locations) {
          const selectedCircle = circles.find(
            (circle) => circle.id === location.circleId
          );

          // Build stageMapping as { AccuLynxStage: paivStageId }
          const stageMapping: Record<string, string> = {};
          for (const m of location.stageMappings || []) {
            if (m.accuLynxStage && m.paivStageId) {
              stageMapping[m.accuLynxStage] = m.paivStageId;
            }
          }

          const payload = {
            orgID,
            apiKey: location.apiKey,
            categoryID: (location as any)?.stageCategoryID || null,
            stageMapping,
            configOptions: {
              circleId: location.circleId || '',
              circleName:
                selectedCircle?.name ||
                (location as any)?.circleName ||
                'Unknown',
              circleLevelID: (location as any)?.circleLevelID || '',
              ...(location.contactTypeId && {
                contactTypeId: location.contactTypeId,
              }),
            },
          };

          const existingId = (location as any)?.id;

          try {
            let res: Response;
            if (existingId) {
              // PUT to update existing config
              res = await fetch(`${ACCULYNX_CONFIG_API_BASE}/${existingId}`, {
                method: 'PUT',
                headers: authHeaders,
                body: JSON.stringify(payload),
              });
            } else {
              // POST to create new config
              res = await fetch(ACCULYNX_CONFIG_API_BASE, {
                method: 'POST',
                headers: authHeaders,
                body: JSON.stringify(payload),
              });
            }

            const result = await res.json();
            if (!res.ok || !result?.success) {
              errors.push({
                locationId: location.circleId,
                error: result?.error || `Failed: ${res.status}`,
              });
            } else {
              results.push(result);
              if (result.apiKeyValid === false) {
                warnings.push({ locationId: location.circleId });
              }
            }
          } catch (fetchError: any) {
            errors.push({
              locationId: location.circleId,
              error: fetchError?.message || 'Network error',
            });
          }
        }

        if (errors.length === 0) {
          // Build nextConfig reflecting API key validation warnings
          let nextConfig: IntegrationConfig;
          if (warnings.length > 0) {
            const warned = new Set(
              warnings.map((w: any) => w?.locationId).filter(Boolean)
            );
            nextConfig = {
              ...config,
              locations: config.locations.map((loc, idx) => ({
                ...loc,
                id: (loc as any)?.id || results[idx]?.id,
                connectionStatus: warned.has(loc.circleId)
                  ? ('failed' as const)
                  : ('connected' as const),
                connectionError: warned.has(loc.circleId)
                  ? 'API key may be invalid'
                  : undefined,
              })),
            };
          } else {
            nextConfig = {
              ...config,
              locations: config.locations.map((loc, idx) => ({
                ...loc,
                id: (loc as any)?.id || results[idx]?.id,
                connectionStatus: 'connected' as const,
                connectionError: undefined,
              })),
            };
          }

          setConfig(nextConfig);
          setSavedConfig(nextConfig);
          setIsEditing(false);

          setSubmitStatus({
            type: warnings.length ? 'info' : 'success',
            message: warnings.length
              ? `Saved. ${warnings.length} API key${
                  warnings.length > 1 ? 's' : ''
                } may be invalid.`
              : 'Integration saved successfully!',
          });
        } else {
          setSubmitStatus({
            type: 'error',
            message: `${results.length} saved, ${errors.length} failed: ${errors[0]?.error}`,
          });
        }
      } catch (error) {
        console.error('Error in handleSubmit:', error);
        setSubmitStatus({
          type: 'error',
          message:
            error instanceof Error
              ? `Error: ${error.message}`
              : 'There was an error saving your integration. Please try again.',
        });
      } finally {
        setIsSubmitting(false);
      }
    },
    [config, circles, setSavedConfig, setIsEditing]
  );

  // Handle cancel
  const handleCancel = useCallback(() => {
    setConfig(savedConfig);
    setIsEditing(false);
    setSubmitStatus({ type: undefined, message: '' });
  }, [savedConfig, setConfig, setIsEditing]);

  // Handle get started
  const handleGetStarted = useCallback(() => {
    console.log('handleGetStarted called');
    setIsEditing(true);
  }, [setIsEditing]);

  // Detect if config has changed from saved state
  const hasChanges = useMemo(() => {
    // New integration (no saved locations) — always has changes if there's content
    if (savedConfig.locations.length === 0) {
      return config.locations.some((loc) => loc.apiKey?.trim());
    }
    // Compare serialized state for deep equality
    const normalize = (locations: any[]) =>
      locations.map((loc) => ({
        apiKey: loc.apiKey || '',
        circleId: loc.circleId || '',
        circleLevelID: loc.circleLevelID || '',
        stageCategoryID: loc.stageCategoryID || '',
        contactTypeId: loc.contactTypeId || '',
        stageMappings: (loc.stageMappings || [])
          .map((m: any) => `${m.accuLynxStage}:${m.paivStageId || ''}`)
          .sort(),
      }));
    try {
      return (
        JSON.stringify(normalize(config.locations)) !==
        JSON.stringify(normalize(savedConfig.locations))
      );
    } catch {
      return false;
    }
  }, [config, savedConfig]);

  return {
    // State
    isSubmitting,
    submitStatus,
    hasChanges,

    // Actions
    addLocation,
    removeLocation,
    removeIntegration,
    performIntegrationRemoval,
    updateLocation,
    handleApiKeyChange,
    updateStageMapping,
    validateConfig,
    handleSubmit,
    handleCancel,
    handleGetStarted,

    // Setters
    setSubmitStatus,
  };
};
